/**
 * Centralized query keys factory for type-safe query key management.
 * Each entity has its own namespace with standardized query key patterns.
 *
 * Queries are defined by `queryOptions` factories that consume these keys, in
 * `src/features/marquee/query-options.ts`.
 *
 * Keys are hierarchical: every child extends its parent's prefix, so
 * invalidating a parent reaches all of its children. Mutations invalidate the
 * smallest prefix that covers what changed, and never a parent together with
 * one of its own children.
 *
 * Scoped to Marquee because the SDK is scoped to Marquee. When a second feature
 * moves onto the SDK, promote this to `src/query-keys.ts` and namespace it per
 * feature rather than growing a second map.
 *
 * @example
 * ```typescript
 * // Read: a queryOptions factory built on these keys
 * const event = useQuery(eventQueryOptions('123'))
 *
 * // Read the list (unfiltered: tabs and search are applied client-side)
 * const events = useQuery(eventListQueryOptions())
 *
 * // Invalidate one event, its roster and its export preview (prefix matching)
 * queryClient.invalidateQueries({
 *   queryKey: makeQueryKeys.marquee.events.detail('123')
 * })
 *
 * // Invalidate every event query, list included: for a create, or for a
 * // mutation whose effect shows in the tracker list (state, gaps to resolve)
 * queryClient.invalidateQueries({
 *   queryKey: makeQueryKeys.marquee.events.all()
 * })
 * ```
 */
export const makeQueryKeys = {
	marquee: {
		events: {
			all: () => ['marquee', 'events'] as const,
			index: () => ['marquee', 'events', 'index'] as const,
			detail: (eventId: string) =>
				['marquee', 'events', 'detail', eventId] as const,
			attendees: (eventId: string) =>
				['marquee', 'events', 'detail', eventId, 'attendees'] as const,
			exportPreview: (eventId: string) =>
				[
					'marquee',
					'events',
					'detail',
					eventId,
					'export-preview'
				] as const
		},
		markets: {
			all: () => ['marquee', 'markets'] as const
		},
		/**
		 * The signed-in operator. Session data, not event data: it answers who
		 * is at the keyboard and what they may change, so no event mutation
		 * ever invalidates it.
		 */
		operator: {
			current: () => ['marquee', 'operator', 'current'] as const
		},
		// Reference data, not event data: the host directory is the resolver's,
		// shared by every event, so an event mutation must never invalidate it.
		hosts: {
			all: () => ['marquee', 'hosts'] as const,
			search: (query: string) =>
				['marquee', 'hosts', 'search', query] as const
		},
		ctas: {
			all: () => ['marquee', 'ctas'] as const
		},
		/**
		 * The host ID to Salesforce account snapshot every event resolves
		 * against. Reference data like the host directory, not event data: one
		 * global table shared by every event, so no event mutation invalidates
		 * it, and replacing it invalidates nothing else here (a roster already
		 * on screen keeps the resolution it was read with).
		 */
		resolutionSource: {
			all: () => ['marquee', 'resolution-source'] as const,
			status: () => ['marquee', 'resolution-source', 'status'] as const
		},
		marketManagers: {
			all: () => ['marquee', 'market-managers'] as const
		},
		/**
		 * The operator roster the Team screen administers. Its own namespace,
		 * not under `operator`: a roster write must never refetch the session's
		 * own answer, which is held for the whole shift. `marketManagers` reads
		 * the same roster (`is_market_manager`), so every roster write
		 * invalidates it as well.
		 */
		roster: {
			all: () => ['marquee', 'roster'] as const,
			index: () => ['marquee', 'roster', 'index'] as const
		},
		/**
		 * Custom locations: reference data for the event form's market picker
		 * (active only) and the admin Locations screen (`includeInactive`).
		 * Every Locations write invalidates `all()`, so both lists refetch.
		 */
		locations: {
			all: () => ['marquee', 'locations'] as const,
			index: (filters: { includeInactive: boolean }) =>
				['marquee', 'locations', 'index', filters] as const
		}
	}
} as const
